import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, Check, X, ExternalLink, Copy, Sparkles, Gauge, HardDrive, FileText, Mic, Image as ImageIcon, MessageCircle, Zap, Clock, ArrowRight, ShieldCheck } from "lucide-react";
import api from "../lib/api";

const LIMIT_ICONS = {
  brands: FileText,
  posts_per_generation: Zap,
  platforms_per_post: Activity,
  upload_file_size: HardDrive,
  video_duration: Mic,
  interview_questions: MessageCircle,
  ab_split_wait_hours: Clock,
  year_gen_quality_floor: Sparkles,
};

const LIMIT_LABELS = {
  brands: "Brands",
  posts_per_generation: "Posts per generation job",
  platforms_per_post: "Social platforms per post",
  upload_file_size: "Upload file size",
  video_duration: "Video / audio duration",
  interview_questions: "Interview Q&As",
  ab_split_wait_hours: "A/B test wait window",
  year_gen_quality_floor: "Year-gen quality floor",
};

export default function Limits() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.systemLimits().then(setData).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm mono uppercase text-[#A1A1AA]">loading…</div>;

  const planActive = data.plan.status === "active" || data.plan.status === "trialing";

  return (
    <div className="max-w-6xl space-y-8" data-testid="limits-page">
      <div>
        <div className="text-eyebrow mb-2 flex items-center gap-2"><Gauge size={12}/> System limits</div>
        <h1 className="font-display font-black tracking-tighter text-5xl">Everything you can and can't do.</h1>
        <p className="text-sm text-[#52525B] mt-3 max-w-2xl">Hard caps, current usage, and the no-key alternative wherever a third-party integration is missing. No surprises.</p>
      </div>

      {/* PLAN BADGE */}
      <div className={`border-2 ${planActive ? "border-black bg-white" : "border-[#F59E0B] bg-[#FFFBEB]"} p-5 flex items-center justify-between flex-wrap gap-3`} data-testid="plan-badge">
        <div>
          <div className="text-eyebrow mb-1">Your plan</div>
          <div className="font-display font-black text-3xl tracking-tighter">
            {data.plan.is_creator ? "Creator (free Mogul)" : data.plan.tier ? data.plan.tier.toUpperCase() : "NO PLAN"}
          </div>
          <div className="text-sm text-[#52525B] mt-1">{data.plan.label}</div>
        </div>
        {!planActive && (
          <button onClick={() => navigate("/pricing")} className="bg-black text-white px-5 py-3 text-sm hover:bg-[#002FA7] flex items-center gap-2" data-testid="upgrade-button">
            See plans <ArrowRight size={14}/>
          </button>
        )}
      </div>

      {/* USAGE SUMMARY */}
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="usage-summary">
        <UsageCard icon={FileText} label="Brands" value={data.usage.brands} cap={data.limits.brands.cap} unit="brands"/>
        <UsageCard icon={Activity} label="Posts" value={data.usage.posts_total} sub={`${data.usage.posts_published} published`}/>
        <UsageCard icon={MessageCircle} label="Interview Q&As" value={data.usage.interview_qa} sub={`${data.limits.year_gen_quality_floor.min} needed per brand to unlock year-gen`}/>
        <UsageCard icon={HardDrive} label="Storage" value={data.usage.storage_label} sub={`${data.usage.knowledge_files} file${data.usage.knowledge_files === 1 ? "" : "s"} uploaded`}/>
      </div>

      {/* LIMITS TABLE */}
      <div className="border-2 border-black bg-white" data-testid="limits-table">
        <div className="border-b border-black p-4 bg-[#FAFAFA]">
          <div className="text-eyebrow flex items-center gap-2"><Gauge size={12}/> Hard limits</div>
        </div>
        <div className="divide-y divide-[#E2E4E9]">
          {Object.entries(data.limits).map(([key, limit]) => {
            const Icon = LIMIT_ICONS[key] || Gauge;
            return (
              <div key={key} className="p-4 grid grid-cols-12 gap-4 items-start hover:bg-[#FAFAFA] transition-colors" data-testid={`limit-${key}`}>
                <div className="col-span-12 md:col-span-3 flex items-center gap-2">
                  <Icon size={14} className="text-[#52525B]"/>
                  <span className="font-semibold text-sm">{LIMIT_LABELS[key] || key}</span>
                </div>
                <div className="col-span-6 md:col-span-3 mono text-sm">
                  {limit.cap !== undefined ? (limit.cap === null ? "Unlimited" : `${limit.cap}${limit.unit ? " " + limit.unit : ""}`) :
                   limit.cap_label ? `${limit.cap_label} ${limit.unit || ""}` :
                   limit.cap_bytes ? `${limit.cap_label} ${limit.unit}` :
                   limit.cap_minutes ? `${limit.cap_minutes} ${limit.unit}` :
                   limit.min ? `Min ${limit.min} ${limit.unit}` : "—"}
                </div>
                <div className="col-span-6 md:col-span-2 text-xs text-[#52525B]">
                  {limit.used !== undefined && `Used: ${limit.used}`}
                </div>
                <div className="col-span-12 md:col-span-4 text-xs text-[#52525B] leading-relaxed">
                  {limit.note}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* INTEGRATIONS — keyless alternatives */}
      <div>
        <div className="text-eyebrow mb-3 flex items-center gap-2"><ShieldCheck size={12}/> Integrations · keyless options for everything</div>
        <h3 className="font-display font-black text-3xl tracking-tighter mb-2">Use it without any third-party key.</h3>
        <p className="text-sm text-[#52525B] mb-6 max-w-2xl">Every paid integration has a "no-key" path. You don't need to subscribe to anything outside Vitality to get value.</p>
        <div className="grid md:grid-cols-2 gap-4" data-testid="integrations-grid">
          {data.integrations.map((i) => (
            <IntegrationCard key={i.id} integration={i}/>
          ))}
        </div>
      </div>

      {/* BACKGROUND JOBS — what takes time */}
      <div className="border border-black p-6" data-testid="background-jobs">
        <div className="text-eyebrow mb-2 flex items-center gap-2"><Clock size={12}/> Background jobs — what to expect</div>
        <h3 className="font-display font-black text-2xl tracking-tighter mb-4">How long things take.</h3>
        <div className="space-y-3 text-sm">
          <JobRow label="One-click year generation (52 posts)" time={`~${Math.round(data.background_jobs.year_generation.total_seconds_year / 60)} min`} detail="Runs in background. Progress bar in Brand → Year Generation panel."/>
          <JobRow label="Video transcription (Whisper)" time={`~${data.background_jobs.video_transcription.avg_seconds_per_min_audio} sec per minute of audio`} detail="No live progress bar yet — upload returns when done. 60-min video ≈ 4 min wait."/>
          <JobRow label="Cover image generation (Nano Banana)" time={`~${data.background_jobs.cover_image_generation.avg_seconds} sec`} detail="Click 'Regenerate' until you love it. Free with your plan."/>
          <JobRow label="AI subject variants" time="~3-5 sec" detail="Returns 3 alternatives with style + predicted score."/>
        </div>
      </div>
    </div>
  );
}

function UsageCard({ icon: Icon, label, value, cap, unit, sub }) {
  return (
    <div className="border border-black bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-eyebrow">{label}</div>
        <Icon size={14} className="text-[#52525B]"/>
      </div>
      <div className="font-display font-black text-3xl tracking-tighter">{value}{cap !== undefined && cap !== null && <span className="text-[#A1A1AA] text-lg"> / {cap}</span>}{cap === null && <span className="text-[#A1A1AA] text-base"> / ∞</span>}</div>
      {sub && <div className="text-xs text-[#52525B] mt-1">{sub}</div>}
    </div>
  );
}

function IntegrationCard({ integration: i }) {
  const connected = i.connected;
  const required = i.required;
  return (
    <div className={`border-2 ${connected ? "border-[#059669] bg-[#ECFDF5]" : required ? "border-[#F59E0B] bg-[#FFFBEB]" : "border-black bg-white"} p-5`} data-testid={`integration-${i.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-display font-bold text-base">{i.name}</span>
            {connected ? <span className="text-xs mono px-1.5 py-0.5 bg-[#059669] text-white">CONNECTED</span> :
             required ? <span className="text-xs mono px-1.5 py-0.5 bg-[#F59E0B] text-black">NEEDS SETUP</span> :
             <span className="text-xs mono px-1.5 py-0.5 border border-[#52525B] text-[#52525B]">OPTIONAL</span>}
          </div>
          <div className="text-xs text-[#52525B]">{i.scope} {i.free && <span className="mono text-[#059669] ml-1">· FREE</span>}</div>
        </div>
      </div>
      <div className="text-xs text-[#52525B] mb-3">{i.status_detail}</div>
      {i.keyless_alternative && !connected && (
        <div className="border border-black bg-white p-3 mt-3">
          <div className="text-eyebrow mb-1 text-[#0A0A0B]">No-key alternative</div>
          <div className="text-xs leading-relaxed">{i.keyless_alternative}</div>
        </div>
      )}
      {i.where_to_set && (
        <div className="text-[10px] mono text-[#A1A1AA] mt-3 leading-relaxed">→ {i.where_to_set}</div>
      )}
    </div>
  );
}

function JobRow({ label, time, detail }) {
  return (
    <div className="grid grid-cols-12 gap-3 border-b border-[#E2E4E9] pb-3 last:border-0">
      <div className="col-span-12 md:col-span-5 font-semibold">{label}</div>
      <div className="col-span-4 md:col-span-2 mono text-[#059669]">{time}</div>
      <div className="col-span-8 md:col-span-5 text-xs text-[#52525B]">{detail}</div>
    </div>
  );
}
